import { BadgeCheck, BadgeX } from 'lucide-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Pie, PieChart } from 'recharts';
import { isEmpty } from 'lodash';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatBytes } from 'src/utils/formatBytes';
import { getRandomColors } from 'src/utils/getRandomColors';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { cn } from 'src/lib/utils';

import { type IAssetReport } from '../model';

type Props = {
  assetReport: IAssetReport;
};

const prepareChartData = (languages: Record<string, number>, colors: string[]) => {
  const chartData: { language: string; percentage: number; fill: string }[] = [];
  const chartConfig: ChartConfig = {};

  Object.entries(languages).forEach(([language, percentage], index) => {
    const color = colors[index] || '#E2E8EB'; // fallback color
    chartData.push({ language, percentage, fill: color });
    chartConfig[language] = { label: language, color };
  });

  return { chartData, chartConfig };
};

const EmptyChart = () => {
  const chartData = [
    { language: '1', percentage: 40, fill: '#758D9B' },
    { language: '2', percentage: 30, fill: '#A2B4BE' },
    { language: '3', percentage: 20, fill: '#C8D3D9' },
    { language: '4', percentage: 10, fill: '#E2E8EB' },
  ];

  return (
    <ChartContainer config={{}} className="shrink-0 aspect-square w-[173px] max-h-[173px]">
      <PieChart>
        <Pie data={chartData} dataKey="percentage" nameKey="language" innerRadius={30} />
      </PieChart>
    </ChartContainer>
  );
};

const Chart = ({ languages, colors }: { languages: Record<string, number>; colors: string[] }) => {
  const { chartData, chartConfig } = useMemo(() => prepareChartData(languages, colors), [languages, colors]);

  return (
    <ChartContainer config={chartConfig} className="shrink-0 aspect-square w-[173px] max-h-[173px]">
      <PieChart>
        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
        <Pie data={chartData} dataKey="percentage" nameKey="language" innerRadius={30} />
      </PieChart>
    </ChartContainer>
  );
};

const ChartLegendItem = ({
  label,
  color,
  value,
  size = 'sm',
}: {
  label: string;
  color: string;
  value: number | null;
  size?: 'sm' | 'md';
}) => {
  return (
    <div className="space-y-1.5">
      <div className={cn('rounded-full  w-full', size === 'sm' ? 'h-1' : 'h-1.5')} style={{ backgroundColor: color }} />
      <div className={cn('text-foreground font-bold', size === 'sm' ? 'text-[11px]' : 'text-base')}>
        {value !== null ? `${value}%` : '-'}
      </div>
      <div className={cn('text-muted-foreground  uppercase truncate', size === 'sm' ? 'text-[7px]' : 'text-[9px]')}>
        {value !== null ? label : null}
      </div>
    </div>
  );
};
const ChartLegend = ({ languages, colors }: { languages: Record<string, number>; colors: string[] }) => {
  const items = Object.keys(languages).map((language, index) => ({
    label: language,
    value: languages[language],
    color: colors[index],
  }));
  const emptyItems = [
    { label: '1', color: '#758D9B', value: null },
    { label: '2', color: '#A2B4BE', value: null },
    { label: '3', color: '#C8D3D9', value: null },
    { label: '4', color: '#E2E8EB', value: null },
  ];

  if (isEmpty(items))
    return (
      <div className="grow-1 space-y-3.5">
        <div className="grid grid-cols-4 gap-y-5 gap-x-4">
          {emptyItems.map(({ label, color, value }) => (
            <ChartLegendItem key={label} label={label} color={color} value={value} size="md" />
          ))}
        </div>
      </div>
    );

  return (
    <div className="grow-1 space-y-3.5">
      <div className="grid grid-cols-4 gap-y-5 gap-x-4">
        {items.slice(0, 4).map(({ label, color, value }) => (
          <ChartLegendItem key={label} label={label} color={color} value={value} size="md" />
        ))}
      </div>
      <div className="grid grid-cols-6 gap-y-5 gap-x-4">
        {items.slice(4).map(({ label, color, value }) => (
          <ChartLegendItem key={label} label={label} color={color} value={value} />
        ))}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value }: { label: string; value: string | number }) => {
  return (
    <div className="bg-[rgba(244,246,247,0.60)] rounded p-3.5 space-y-2">
      <div className="text-[10px] text-foreground opacity-80">{label}</div>
      <div className="text-foreground text-xl font-bold">{value}</div>
    </div>
  );
};

const ContentVerification = ({ assetReport }: Props) => {
  const languages = assetReport?.data.languages || {};
  const colors = useMemo(() => getRandomColors(Object.keys(languages).length), [languages]);
  const sourceCodeMap = {
    identified: {
      label: 'Source code identified',
      badge: {
        className: 'text-[#166534] bg-[#DCFCE7]',
        icon: BadgeCheck,
      },
    },
    notIdentified: {
      label: 'Source code not identified',
      badge: {
        className: 'text-[#9F1239] bg-[#FFE4E6]',
        icon: BadgeX,
      },
    },
  };
  const {
    badge: { className: badgeClassName, icon: BadgeIcon },
    label,
  } = isEmpty(languages) ? sourceCodeMap.notIdentified : sourceCodeMap.identified;

  return (
    <div>
      <div className="flex items-center justify-start gap-2.25 mb-3">
        <h2 className="text-sm text-foreground">{assetReport?.asset.depositSource}</h2>
        <Badge className={cn(badgeClassName, 'gap-1 text-[10px]')}>
          <BadgeIcon className="size-2.75" />
          {label}
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <InfoCard label="Repository size" value={formatBytes(assetReport?.data.size ?? 0)} />
        <InfoCard label="Collaborators" value={assetReport?.data.collaborators || 0} />
        <InfoCard
          label={`Changes in ${dayjs(assetReport?.period).format('MMMM')}`}
          value={assetReport?.data.totalCommits || 0}
        />
      </div>
      <Separator className="my-6" />
      <div className="text-foreground text-sm font-bold mb-4">Content Profile</div>
      <div className="flex gap-8">
        <ChartLegend languages={languages} colors={colors} />
        {isEmpty(languages) ? <EmptyChart /> : <Chart languages={languages} colors={colors} />}
      </div>
    </div>
  );
};

export default ContentVerification;
